import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

const CustomModal = ({ isOpen, onClose, children, title, disableClose = false, hideCloseButton = false }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = e => {
      if (!disableClose && e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = e => {
    if (!disableClose && e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4">
      {/* Backdrop with blur effect */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md transition-opacity duration-300" onClick={handleBackdropClick} />

      {/* Modal content */}
      <div
        className="relative w-full h-full md:max-w-md md:mx-auto md:transform md:transition-all md:duration-300 md:scale-100 md:max-h-[90vh] md:h-auto flex flex-col z-10"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal container */}
        <div className="bg-white flex flex-col h-full md:rounded-2xl md:shadow-2xl md:border md:border-gray-100 md:overflow-hidden md:max-h-full md:h-auto">
          {/* Header */}
          {title && (
            <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {!hideCloseButton && (
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CustomModal;
